import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ReactComponent as RefreshIcon } from '../../../assets/Refresh_icon.svg';
import './RSAAnalysis.css';
import TabsRSA from './Services/TabsRSA/TabsRSA';
import { API_BASE_URL, ENVIRONMENT } from '../../../API/api';
// Using relative URLs for production (CloudFront), API_BASE_URL for local development

// Add debugging to intercept all fetch/axios calls
(() => {
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    const url = args[0];
    if (typeof url === 'string' && url.includes('null')) {
      console.trace('🔍 NULL FETCH CALL DETECTED:', url);
      console.trace('🔍 Stack trace:', new Error().stack);
    }
    return originalFetch(...args);
  };
  
  const originalAxiosGet = axios.get;
  axios.get = (...args) => {
    const url = args[0];
    if (typeof url === 'string' && url.includes('null')) {
      console.trace('🔍 NULL AXIOS GET CALL DETECTED:', url);
      console.trace('🔍 Stack trace:', new Error().stack);
    }
    return originalAxiosGet(...args);
  };
})();

// --- Recursive tree node component ---
function FolderTreeNode({ folder, onSelect, selectedFolderId, parentPath = '' }) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleExpand = async (e) => {
    // Prevent the click from triggering selection and form submission
    e.preventDefault();
    e.stopPropagation();
    if (!expanded) {
      setLoading(true);
      try {
        const url = ENVIRONMENT === 'local' 
          ? `${API_BASE_URL}/api/google-drive/folders?parent_id=${folder.id}`
          : `/api/google-drive/folders?parent_id=${folder.id}`;
        console.log("🔧 RSAAnalysis - Making folder request to:", url);
        const response = await axios.get(url);
        setChildren(response.data.folders);
      } catch (err) {
        console.error("❌ RSAAnalysis - Error fetching children for folder", folder.id, err);
      }
      setLoading(false);
    }
    setExpanded(!expanded);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fullPath = parentPath ? `${parentPath}/${folder.name}` : folder.name;
    onSelect({ ...folder, fullPath });
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleExpand(e);
  };

  const isSelected = folder.id === selectedFolderId;

  return (
    <li className={`rsa-tree-node ${isSelected ? 'rsa-tree-node-selected' : ''}`}>
      <div 
        className="rsa-tree-node-content"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <button 
          type="button"
          className={`rsa-tree-toggle ${expanded ? 'rsa-tree-toggle-expanded' : ''}`}
          onClick={toggleExpand}
          disabled={loading}
          aria-label={expanded ? 'Collapse folder' : 'Expand folder'}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="rsa-tree-icon">
          {expanded ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
        
        <span className="rsa-tree-label">{folder.name}</span>
        
        {loading && (
          <span className="rsa-tree-loading">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="rsa-tree-loading-spinner">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
              </circle>
            </svg>
          </span>
        )}
      </div>
      
      {expanded && children.length > 0 && (
        <ul className="rsa-tree-children">
          {children.map(child => (
            <FolderTreeNode 
              key={child.id} 
              folder={child} 
              onSelect={onSelect} 
              selectedFolderId={selectedFolderId} 
              parentPath={parentPath ? `${parentPath}/${folder.name}` : folder.name}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// --- Modal popup with the folder tree and shared drive selector ---
function FolderTreePopup({ onClose, onFolderSelect, onDriveSelect }) {
  // State for shared drives
  const [sharedDrives, setSharedDrives] = useState([]);
  const [selectedDrive, setSelectedDrive] = useState('');
  // Root folder id used to fetch folders (either shared drive id or manually entered)
  const [rootFolderId, setRootFolderId] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch available shared drives from backend
  const fetchSharedDrives = async () => { 
    try {
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/google-drive/shared-drives`
        : `/api/google-drive/shared-drives`;
      const response = await axios.get(url);
      // Handle both response formats and ensure we always have an array
      const drives = response.data?.drives || response.data?.shared_drives || [];
      const drivesArray = Array.isArray(drives) ? drives : [];
      setSharedDrives(drivesArray);
      // Update cache
      sessionStorage.setItem('sharedDrives', JSON.stringify(drivesArray));
    } catch (err) {
      console.error("Error fetching shared drives", err);
      // Set empty array on error to prevent component crash
      setSharedDrives([]);
    }
  };

  // Fetch subfolders for the current rootFolderId
  const fetchFolders = async (folderId) => {
    setLoading(true);
    try {
      console.log("🔧 RSAAnalysis - Fetching folders for parent_id:", folderId);
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/google-drive/folders?parent_id=${folderId}`
        : `/api/google-drive/folders?parent_id=${folderId}`;
      const response = await axios.get(url);
      console.log("🔧 RSAAnalysis - Folders response:", response.data);
      // Ensure we always have an array
      const folders = response.data?.folders || [];
      setFolders(Array.isArray(folders) ? folders : []);
      console.log(`✅ RSAAnalysis - Set ${folders.length} folders`);
    } catch (err) {
      console.error("❌ RSAAnalysis - Error fetching folders for", folderId, err);
      // Set empty array on error to prevent component crash
      setFolders([]);
    }
    setLoading(false);
  };

  // On mount, load shared drives from cache first, then fetch fresh data
  useEffect(() => {
    // First, try to load from sessionStorage (preloaded by parent component)
    const cachedDrives = sessionStorage.getItem('sharedDrives');
    if (cachedDrives) {
      try {
        const parsedDrives = JSON.parse(cachedDrives);
        setSharedDrives(Array.isArray(parsedDrives) ? parsedDrives : []);
        console.log("✅ FolderTreePopup - Shared drives loaded from cache:", parsedDrives.length);
      } catch (e) {
        console.error("Error parsing cached shared drives:", e);
      }
    }
    
    // Always fetch fresh data in the background
    fetchSharedDrives();
  }, []);

  // When a shared drive is selected, update the rootFolderId and fetch its folders
  useEffect(() => {
    if (selectedDrive) {
      setRootFolderId(selectedDrive);
      fetchFolders(selectedDrive);
      // Get the drive name and pass it to parent
      const drive = sharedDrives.find(d => d.id === selectedDrive);
      if (drive && onDriveSelect) {
        onDriveSelect(drive.name);
      }
    }
  }, [selectedDrive, sharedDrives, onDriveSelect]);

  const handleSelect = (folder) => {
    setSelectedFolder(folder);
  };

  const handleConfirm = () => {
    if (selectedFolder) {
      onFolderSelect(selectedFolder);
      onClose();
    } else {
      alert("Please select a folder.");
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    // Refresh shared drives
    await fetchSharedDrives();
    // If a drive is selected, refresh its folders
    if (rootFolderId) {
      await fetchFolders(rootFolderId);
    }
    setLoading(false);
  };

  return (
          <div className="TreeModalOverlay">
        <div className="TreeModalContainer">
        <div className="modal-header">
          <h2 className="modal-title">Select LAS Folder</h2>
        </div>
        
        <div className="modal-content">
          {/* Shared Drive Selector */}
          <div className="modal-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label className="modal-label">Select a Shared Drive:</label>
              <button
                type="button"
                onClick={handleRefresh}
                className="rsa-modal-refresh-btn"
                title="Refresh Google Drive"
              >
                <RefreshIcon className="rsa-modal-refresh-icon" />
              </button>
            </div>
            <select
              className="modal-select"
              value={selectedDrive}
              onChange={(e) => setSelectedDrive(e.target.value)}
            >
              <option value="" className="modal-option">-- Select Shared Drive --</option>
              {Array.isArray(sharedDrives) && sharedDrives.length > 0 ? (
                sharedDrives.map((drive) => (
                  <option key={drive.id} value={drive.id} className="modal-option">
                    {drive.name}
                  </option>
                ))
              ) : (
                <option value="" className="modal-option" disabled>No shared drives available</option>
              )}
            </select>
          </div>
          
          {loading ? (
            <div className="modal-loading">Loading folders...</div>
          ) : (
            Array.isArray(folders) && folders.length > 0 ? (
              <ul className="tree">
                {folders.map(folder => (
                  <FolderTreeNode 
                    key={folder.id} 
                    folder={folder} 
                    onSelect={handleSelect} 
                    selectedFolderId={selectedFolder?.id}
                    parentPath=""
                  />
                ))}
              </ul>
            ) : selectedDrive ? (
              <div className="modal-loading" style={{ color: '#94a3b8', fontSize: '14px' }}>
                No folders found in this shared drive. Please select a different drive.
              </div>
            ) : (
              <div className="modal-loading" style={{ color: '#94a3b8', fontSize: '14px' }}>
                Please select a shared drive to view folders.
              </div>
            )
          )}
        </div>

        <div className="modal-actions">
          <button className="modal-btn modal-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="modal-btn modal-btn-primary" onClick={handleConfirm}>Select</button>
        </div>
      </div>
    </div>
  );
}

// --- Summary Component ---
function RSASummary({ 
  project, 
  newProjectName, 
  existingProjectName, 
  selectedProjectId,
  services, 
  analysisTypes, 
  selectedFolder, 
  selectedDriveName,
  roadDetectionCategory, 
  lidarType,
  horizontalData,
  EmbankmentData,
  groupSize,
  onSubmit,
  onClear
}) {
  const serviceMapping = {
    'tiling': 'Tiling',
    'csf': 'CSF',
    'road_detection': 'Road Detection',
    'car_removal': 'Car Removal',
    'file_transfer': 'File Transfer',
    'create_rsa': 'Analysis',
    'psd_classification': 'PSD Classification',
    'horizontal': 'Horizontal',
    'embankment': 'Embankment',
    'automatic_reports':'Automatic Reports'
  };

  const analysisMapping = {
    'SSD': 'SSD',
    'PSD': 'PSD',
    'FSA': 'FSA',
    'ISD': 'ISD',
    'GLR': 'GLR',
  };

  return (
    <div className="tabs w35">
      <div className="summary-header">
        <h3>Analysis Selection Summary</h3>
      </div>
      <div className="summary-content">
        <div className="summary-section">
          <h4>Project Details</h4>
          <div className="summary-item">
            <span className="summary-label">Type:</span>
            <span className="summary-value">{project === 'new' ? 'New Project' : 'Existing Project'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Name:</span>
            <span className="summary-value">
              {project === 'new' ? newProjectName : existingProjectName}
            </span>
          </div>
          {project === 'existing' && selectedProjectId && (
            <div className="summary-item">
              <span className="summary-label">Project ID:</span>
              <span className="summary-value">{selectedProjectId}</span>
            </div>
          )}
        </div>

        <div className="summary-section">
          <h4>Selected Services</h4>
          {services.length > 0 ? (
            <div className="summary-list">
              {services.map(service => (
                <div key={service} className="summary-tag">
                  {serviceMapping[service] || service}
                </div>
              ))}
            </div>
          ) : (
            <span className="summary-empty">No services selected</span>
          )}
        </div>

        <div className="summary-section">
          <h4>Analysis Types</h4>
          {analysisTypes.length > 0 ? (
            <div className="summary-list">
              {analysisTypes.map(type => (
                <div key={type} className="summary-tag">
                  {analysisMapping[type] || type}
                </div>
              ))}
            </div>
          ) : (
            <span className="summary-empty">No analysis types selected</span>
          )}
        </div>

                 <div className="summary-section">
           <h4>Folder Selection</h4>
           {selectedDriveName && (
             <div className="summary-item">
               <span className="summary-label">Shared Drive:</span>
               <span className="summary-value">{selectedDriveName}</span>
             </div>
           )}
                       {selectedFolder ? (
              <div className="summary-item">
                <span className="summary-label">Folder:</span>
                <span className="summary-value" title={selectedFolder.fullPath || selectedFolder.name}>
                  {(() => {
                    const path = selectedFolder.fullPath || selectedFolder.name;
                    if (path.length > 50) {
                      const firstPart = path.substring(0, 25);
                      const lastPart = path.substring(path.length - 20);
                      return `${firstPart}...${lastPart}`;
                    }
                    return path;
                  })()}
                </span>
              </div>
            ) : (
              <span className="summary-empty">No folder selected</span>
            )}
         </div>

        <div className="summary-section">
          <h4>Configuration</h4>
                     <div className="summary-item">
             <span className="summary-label">LiDAR Type:</span>
             <span className="summary-value">
               {lidarType ? lidarType.charAt(0).toUpperCase() + lidarType.slice(1) : 'Not selected'}
             </span>
           </div>
          <div className="summary-item">
            <span className="summary-label">LAS/LAZ Group Size:</span>
            <span className="summary-value">{groupSize || '25'}</span>
          </div>
        </div>

        {(services.includes('horizontal') || services.includes('embankment')) && (
          <div className="summary-section">
            <h4>Additional Data</h4>
            {services.includes('horizontal') && (
              <div className="summary-item">
                <span className="summary-label">Horizontal Data:</span>
                <span className="summary-value">
                  {horizontalData ? 'Configured' : 'Not configured'}
                </span>
              </div>
            )}
            {services.includes('embankment') && (
              <div className="summary-item">
                <span className="summary-label">Embankment Data:</span>
                <span className="summary-value">
                  {EmbankmentData ? 'Configured' : 'Not configured'}
                </span>
              </div>
            )}
          </div>
                 )}
       </div>
       
               {/* Run Analysis and Clear Buttons at the bottom */}
        <div className="summary-button-container">
          <div className="summary-buttons-row">
            <button className='toggle-btn-rsa-run' type="button" onClick={onSubmit}>
              Run Analysis
            </button>
            <button className='toggle-btn-rsa-clear' type="button" onClick={onClear}>
              Clear
            </button>
          </div>
        </div>
     </div>
   );
 }

function PayloadForm() {
  // Cached project list and dropdown options
  const [allProjects, setAllProjects] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  
  // State for selected project from dropdown
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  // Instead of storing just folderId, store the complete folder object
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedDriveName, setSelectedDriveName] = useState('');
  const [roadDetectionCategory, setRoadDetectionCategory] = useState('');
  const [lidarType, setLidarType] = useState('');
  const [horizontalData, setHorizontalData] = useState(null);
  const [EmbankmentData, setEmbankmentData] = useState(null); // State for Embankment data
  
  // "project" distinguishes between New vs. Existing project
  const [project, setProject] = useState('new'); 
  const [newProjectName, setNewProjectName] = useState('');
  const [existingProjectName, setExistingProjectName] = useState('');

  // State for the analysis types and services (toggle buttons)
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [services, setServices] = useState([]);
  console.log(services);
  // Instead of a global loading state, we now track each payload request.
  const [requests, setRequests] = useState([]);
  // Additional state for our new custom radio button group (e.g. Time selection)
  const [timeOption, setTimeOption] = useState('');

  // --- State for Folder Tree Popup ---
  const [showFolderTreePopup, setShowFolderTreePopup] = useState(false);
  const [groupSize, setGroupSize] = useState(25);
  
  // --- State for custom dropdown ---
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const dropdownRef = useRef(null);
  
  // --- State for clear confirmation popup ---
  const [showClearPopup, setShowClearPopup] = useState(false);

  // Function to fetch projects from the API and reset dropdown state.
  const fetchProjects = async () => {
    console.log("🔧 RSAAnalysis - fetchProjects called");
    setIsLoadingProjects(true);
    try {
      // Use API_BASE_URL for local development, relative URL for production (CloudFront)
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/projectsSearch?query=`
        : '/api/projectsSearch?query=';
      console.log("🔧 RSAAnalysis - Making request to:", url);
      console.log("🔧 RSAAnalysis - ENVIRONMENT:", ENVIRONMENT);
      console.log("🔧 RSAAnalysis - API_BASE_URL:", API_BASE_URL);
      const response = await axios.get(url);
      setAllProjects(response.data);
      sessionStorage.setItem('allProjects', JSON.stringify(response.data));
      // Clear dropdown selections and search results
      setSearchResults([]);
      setSelectedProjectId(null);
      setExistingProjectName('');
      console.log("✅ RSAAnalysis - Projects refreshed from API");
    } catch (error) {
      console.error("❌ RSAAnalysis - Error refreshing projects:", error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Function to fetch shared drives from the API
  const fetchSharedDrives = async () => {
    try {
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/google-drive/shared-drives`
        : '/api/google-drive/shared-drives';
      console.log("🔄 RSAAnalysis - Preloading shared drives from:", url);
      const response = await axios.get(url);
      // Handle both response formats and ensure we always have an array
      const drives = response.data?.drives || response.data?.shared_drives || [];
      const drivesArray = Array.isArray(drives) ? drives : [];
      // Cache in sessionStorage for FolderTreePopup to use
      sessionStorage.setItem('sharedDrives', JSON.stringify(drivesArray));
      console.log("✅ RSAAnalysis - Shared drives preloaded and cached:", drivesArray.length);
    } catch (err) {
      console.error("❌ RSAAnalysis - Error preloading shared drives:", err);
      // Don't show error to user, just log it
    }
  };

  // On mount, preload projects and shared drives from API in the background
  // This ensures the dropdowns are ready when the user opens them
  useEffect(() => {
    // First, load from sessionStorage if available (for immediate display)
    const storedProjects = sessionStorage.getItem('allProjects');
    if (storedProjects) {
      const parsedProjects = JSON.parse(storedProjects);
      setAllProjects(parsedProjects);
      setSearchResults([]); // Keep dropdown empty initially
      console.log("✅ Projects loaded from sessionStorage (cached)");
    }
    
    // Always fetch fresh data in the background on first mount
    // This ensures the dropdowns are ready when user opens them
    console.log("🔄 Preloading projects and shared drives from API on RSA-Analysis mount...");
    fetchProjects().catch(error => {
      console.error("❌ Failed to preload projects:", error);
      // Don't show error to user, just log it - cached data will be used if available
    });
    
    // Preload shared drives in the background
    fetchSharedDrives().catch(error => {
      console.error("❌ Failed to preload shared drives:", error);
      // Don't show error to user, just log it
    });
  }, []);

  // Persist requests in localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('requests', JSON.stringify(requests));
  }, [requests]);

  // Load persisted requests from localStorage on mount
  useEffect(() => {
    const storedRequests = localStorage.getItem('requests');
    if (storedRequests) {
      setRequests(JSON.parse(storedRequests));
    }
  }, []);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update CSS custom properties for dynamic layout when services change
  useEffect(() => {
    const rightPanels = document.querySelector('.right-panels');
    if (rightPanels) {
      const hasEmbankmentPanel = services.includes('horizontal') || services.includes('embankment');
      rightPanels.style.setProperty('--embankment-panel-width', hasEmbankmentPanel ? '380px' : '0px');
    }
  }, [services]);

  // Filter the cached projects locally as the user type.
  const handleInputChange = (inputValue) => {
    console.log('handleInputChange called with:', inputValue);
    console.log('allProjects length:', allProjects.length);
    console.log('allProjects sample:', allProjects.slice(0, 3));
    
    if (!inputValue) {
      setSearchResults([]);
      setDropdownOpen(false);
      return inputValue;
    }
    
    // Always open dropdown when typing
    setDropdownOpen(true);
    
    const filtered = allProjects.filter(proj =>
      proj.name.toLowerCase().includes(inputValue.toLowerCase())
    );
    
    console.log('Filtered results:', filtered);
    console.log('Setting searchResults to:', filtered);
    setSearchResults(filtered);
    
    return inputValue;
  };




  // Modified handleSubmit: use selectedFolder.id for the payload.
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (project === 'new' && !newProjectName.trim()) {
      alert("Please enter new project name.");
      return;
    }
    if (project === 'existing' && !existingProjectName) {
      alert("Please select existing project.");
      return;
    }

    if (services.length === 0) {
      alert("Please select the required service(s).");
      return;
    }

 
    if (!analysisTypes.length === 0) {
      alert("Please select at least one analysis type.");
      return;
    }      
    if (!selectedFolder) {
      alert("Please select a folder from the folder tree.");
      return;
    }
    if (
      (services.includes('horizontal') || services.includes('embankment')) &&
      !analysisTypes.includes('FSA')
    ) {
      alert(
        'In order to run Horizontal or Embankment, FSA analysis is required.'
      );
      return;  
    }
    if (!lidarType) {
      alert("Please select LiDAR type.");
      return;
    }
    
    // Generate a unique id for this request
    const requestId = Date.now();
    
    // Create a new request record with a pending status
    const newRequestEntry = {
      id: requestId,
      projectType: project,
      folderId: selectedFolder.id, // Use folder id from the selected folder
      projectName: project === 'new' ? newProjectName : existingProjectName,
      status: 'pending',
      response: '',
    };
    setRequests(prev => [...prev, newRequestEntry]);
  
    // Choose the csf_params based on the selected road detection category
    const csfParams = lidarType === 'aerial'
      ? { SCENES: "FLAT", CLOTH_RESOLUTION: 1, MAX_ITERATION: 1000, CLASS_THRESHOLD: 0.60, PROC_SLOPE: true }
      : { SCENES: "SLOPE", CLOTH_RESOLUTION: 0.5, MAX_ITERATION: 1000, CLASS_THRESHOLD: 0.15, PROC_SLOPE: true };
  
    try {
      if (project === 'new') {
        // Get user email from sessionStorage or DOM element
        const userEmail = sessionStorage.getItem('userEmail') || 
          (document.querySelector('.user-email')?.textContent?.trim()) || 
          '';
        
        // API Call: Create the New Project
        const newProjectPayload = { 
          accountmanager: "natan.elsberg@rdvsystems.com",
          admin: userEmail || "shafrir.kashi@rdvsystems.com", // Use logged-in user's email, fallback to default
          expiration: "/Date(1924898400000)/",
          members: 99999,
          modules: [1, 2, 8, 32, 64],
          name: newProjectName,
          plan: 8,
          project: ""
        };
  
        await axios.post(
          "https://svrdev.123bim.com/Service.svc/createproject",
          newProjectPayload,
          { headers: { 'Content-Type': 'application/json', 'Auth-Token': 'somekey' } }
        );
  
        // Second API Call: Get the new project ID
        const newProjectIdUrl = ENVIRONMENT === 'local' 
          ? `${API_BASE_URL}/api/new_project_id?name=${encodeURIComponent(newProjectName)}`
          : `/api/new_project_id?name=${encodeURIComponent(newProjectName)}`;
        const newProjectIdResponse = await axios.get(newProjectIdUrl);
        const newProjectId = newProjectIdResponse.data.id;
  
        // Third API Call: Process Payload with New Project ID and Analysis Types.
        
        const processPayload = {
          services: services,
          folder_id: selectedFolder.id,
          lidar_type: lidarType,
          project_id: newProjectId,
          project_name: newProjectName, // Add project name for display
          config_typ_list: analysisTypes,
          csf_params: csfParams,
          group_size: groupSize,
          horizontal_data: horizontalData,
          embankment_data: EmbankmentData,
          user_email: userEmail
        };
  
        const processUrl = ENVIRONMENT === 'local' 
          ? `${API_BASE_URL}/api/process`
          : `/api/process`;
        const processResponse = await axios.post(
          processUrl,
          processPayload,
          { headers: { 'Content-Type': 'application/json' } }
        );

        // Update the specific request record with the response data.
        setRequests(prev =>
          prev.map(req =>
            req.id === requestId
              ? { ...req, status: 'completed', response: `New project created and process completed: ${processResponse.data.message}` }
              : req
          )
        );
      } else if (project === 'existing') {
        // API Call for Existing Projects: Process payload with selected project ID.
        // Get user email from sessionStorage or DOM element
        const userEmail = sessionStorage.getItem('userEmail') || 
          (document.querySelector('.user-email')?.textContent?.trim()) || 
          '';
        
        const processPayload = {
          services: services,
          folder_id: selectedFolder.id,
          lidar_type: lidarType,
          project_id: selectedProjectId,
          project_name: existingProjectName, // Add project name for display
          config_typ_list: analysisTypes,
          csf_params: csfParams,
          group_size: groupSize,
          horizontal_data: horizontalData,
          embankment_data: EmbankmentData,
          user_email: userEmail
        };
  
        const processUrl = ENVIRONMENT === 'local' 
          ? `${API_BASE_URL}/api/process`
          : `/api/process`;
        const processResponse = await axios.post(
          processUrl,
          processPayload,
          { headers: { 'Content-Type': 'application/json' } }
        );

        // Update the request record for the existing project.
        setRequests(prev =>
          prev.map(req =>
            req.id === requestId
              ? { ...req, status: 'completed', response: `Existing project process completed: ${processResponse.data.message}` }
              : req
          )
        );
      }
    } catch (error) {
      // Update the request record with an error message if the API call fails.
      setRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? { ...req, status: 'error', response: error.response?.data?.message || 'There was an error submitting the payload(s).' }
            : req
        )
      );
    }

    // Redirect to dashboard after successful submission
    window.location.href = '/analysis-dashboard';
  };
  
  const serviceMapping = {
    'Tiling': 'tiling',
    'CSF': 'csf',
    'Road Detection': 'road_detection',
    'Car Removal': 'car_removal',
    'File Transfer': 'file_transfer',
    'Analysis': 'create_rsa',
    'PSD Classification': 'psd_classification',
    'Horizontal': 'horizontal',
    'Embankment': 'embankment',
    'Automatic Reports':'automatic_reports'
  };

  // Function to handle clear button click
  const handleClearClick = () => {
    setShowClearPopup(true);
  };

  // Function to clear all form data
  const handleClearConfirm = () => {
    // Reset all form states
    setProject('new');
    setNewProjectName('');
    setExistingProjectName('');
    setSelectedProjectId(null);
    setServices([]);
    setAnalysisTypes([]);
    setSelectedFolder(null);
    setSelectedDriveName('');
    setLidarType('');
    setHorizontalData(null);
    setEmbankmentData(null);
    setGroupSize(25);
    setShowClearPopup(false);
  };

  // Function to cancel clear operation
  const handleClearCancel = () => {
    setShowClearPopup(false);
  };



    return (
    <div className="w100RSA">
      <div className="main-content-area">
        <div className="tab-content-RSA">
          {/* Project Type Selection */}
          <div className="radio-container" style={{ marginBottom: '10px' }}>
            <p className='pHeader'>Select Project Type:</p>
            <div className='radio-content DF'>
              <label className="label-time-selector">
                <input
                  type="radio"
                  name="project"
                  value="new"
                  checked={project === 'new'}
                  onChange={(e) => setProject(e.target.value)}
                />
                <span className="custom-radio-circle"></span>
                <span className="label-text">New Project</span>
              </label>
              <label className="label-time-selector">
                <input
                  type="radio"
                  name="project"
                  value="existing"
                  checked={project === 'existing'}
                  onChange={(e) => setProject(e.target.value)}
                />         
                <span className="custom-radio-circle"></span>
                <span className="label-text">Existing Project</span>
              </label>

            </div> 
          </div>
        {/* Conditionally render input based on selected project type */}
        <div className='project-input-container'>
          {project === 'new' ? (
            <div className='project-input-section'>
              <label>Type New Project Name:</label>
              <div className='project-input-wrapper'>
                <input
                  type="text"
                  id="newProjectName"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Enter Project Name"
                  required
                  className="project-input-field"
                />
              </div>
            </div>
          ) : project === 'existing' ? (
            <div className='project-input-section'>
              <label>Type project name to select :</label>
              <div className='project-input-wrapper'>
                                                   <div className="custom-dropdown" ref={dropdownRef}>
                <div className="input-with-arrow">
                  <input
                    type="text"
                    className="project-input-field"
                    placeholder="Type project name to select"
                    value={existingProjectName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setExistingProjectName(value);
                      handleInputChange(value);
                    }}
                    onFocus={() => setDropdownOpen(true)}
                  />
                  <div 
                    className={`dropdown-arrow ${dropdownOpen ? 'rotated' : ''}`}
                    onClick={() => {
                      if (allProjects.length === 0 && !isLoadingProjects) {
                        fetchProjects();
                      }
                      setDropdownOpen(!dropdownOpen);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                                                                                {dropdownOpen && (
                  <div className="dropdown-menu">
                    {isLoadingProjects ? (
                      // Show loading state
                      <div className="dropdown-option no-results">
                        Loading projects...
                      </div>
                    ) : searchResults.length > 0 ? (
                      // Show filtered results when typing
                      searchResults.map((proj) => (
                        <div
                          key={proj.id}
                          className="dropdown-option"
                          onClick={() => {
                            setSelectedProjectId(proj.id);
                            setExistingProjectName(proj.name);
                            setDropdownOpen(false);
                          }}
                        >
                          {proj.name}
                        </div>
                      ))
                    ) : existingProjectName ? (
                      // Show "no results" when typing but no matches
                      <div className="dropdown-option no-results">
                        No projects found matching "{existingProjectName}"
                      </div>
                    ) : allProjects.length > 0 ? (
                      // Show all projects when input is empty and projects exist
                      allProjects.map((proj) => (
                        <div
                          key={proj.id}
                          className="dropdown-option"
                          onClick={() => {
                            setSelectedProjectId(proj.id);
                            setExistingProjectName(proj.name);
                            setDropdownOpen(false);
                          }}
                        >
                          {proj.name}
                        </div>
                      ))
                    ) : (
                      // Show message when no projects are loaded
                      <div className="dropdown-option no-results">
                        No projects available. Click refresh to load projects.
                      </div>
                    )}
                  </div>
                )}
             </div>
            <button 
              type="button" 
              onClick={() => { fetchProjects(); setExistingProjectName(''); }}
              className="rsa-refresh-btn"
            >
              <RefreshIcon className="rsa-refresh-icon" />
            </button>
          </div>       
        </div>
      ) : (
        <div className='project-input-section'>
          {/* Empty placeholder to maintain consistent height */}
        </div>
      )}
    </div>
         {/* Toggle Buttons for Required Services */}
     <div style={{ marginTop: '30px', marginBottom: '30px', width: 'auto' }}>
      <p className='pHeader'>Select Required Services:</p>
                 <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxWidth: '70%' }}>
        {['Tiling', 'CSF', 'Road Detection', 'Car Removal', 'File Transfer', 'Analysis', 'PSD Classification','Horizontal','Embankment',"Automatic Reports"].map((type, index) => (
          <button
            key={type}
            type="button"
            onClick={() => {
              const serviceValue = serviceMapping[type];
              if (services.includes(serviceValue)) {
                setServices(prev => prev.filter(s => s !== serviceValue));
              } else {
                setServices(prev => [...prev, serviceValue]);
              }
            }}
            className={`toggle-btn-rsa ${services.includes(serviceMapping[type]) ? 'selected' : ''}`}
            style={{
              background: services.includes(serviceMapping[type]) ? 'linear-gradient(135deg,rgba(59, 131, 246, 0) 0%,rgba(8, 56, 189, 0) 100%)' : 'transparent',
              width: 'fit-content',
              padding: '8px 16px',
              whiteSpace: 'nowrap',
              marginBottom: Math.floor(index / 5) > 0 ? '8px' : '0'
            }}
          >
            {type}
          </button>
        ))}
      </div>
    </div>

         {/* Toggle Buttons for Required Analysis Type */}
     <div style={{ marginTop: '30px', marginBottom: '30px' }}>
      <p className='pHeader'>Select Required Analysis Types:</p>
      {['SSD', 'PSD', 'FSA', 'ISD', 'GLR'].map((type) => (
        <button
          key={type}
          type="button"
          onClick={() => {
            if (analysisTypes.includes(type)) {
              setAnalysisTypes(prev => prev.filter(s => s !== type));
            } else {
              setAnalysisTypes(prev => [...prev, type]);
            }
          }}
          className={`toggle-btn-rsa ${analysisTypes.includes(type) ? 'selected' : ''}`}
          style={{
            background: analysisTypes.includes(type) ? 'linear-gradient(135deg,rgba(59, 131, 246, 0) 0%,rgba(8, 56, 189, 0) 100%)' : 'transparent'
          }}
        >
          {type}
        </button>
      ))}
    </div>

    <form onSubmit={handleSubmit}>
      {/* Google Drive Folder Selector via Tree Popup */}
      <div className='content tab-content PB30'>
      <p className='pHeader'>Select Google Drive LAS Folder:</p>
        <div className="DF">
                         <button className='toggle-btn-las' type="button" onClick={() => setShowFolderTreePopup(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V9C21 7.89543 20.1046 7 19 7H12L10 5H5C3.89543 5 3 5.89543 3 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 14L12 11L15 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

                     {/* Render the Folder Tree Popup Modal */}
        {showFolderTreePopup && (
          <FolderTreePopup 
            onClose={() => setShowFolderTreePopup(false)}
            onFolderSelect={(folder) => setSelectedFolder(folder)}
            onDriveSelect={(driveName) => setSelectedDriveName(driveName)}
          />
        )}

       {/* Render the Clear Confirmation Popup Modal */}
       {showClearPopup && (
         <div className="TreeModalOverlay">
           <div className="TreeModalContainer">
             <div className="modal-header">
               <h2 className="modal-title">Clear Form</h2>
             </div>
             
             <div className="modal-content">
               <p style={{ textAlign: 'center', color: '#e2e8f0', fontSize: '16px', margin: '20px 0' }}>
                 Are you sure you want to completely clear the page? This action cannot be undone.
               </p>
             </div>

             <div className="modal-actions">
               <button className="modal-btn modal-btn-secondary" onClick={handleClearCancel}>
                 Cancel
               </button>
               <button className="modal-btn modal-btn-primary" onClick={handleClearConfirm}>
                 Yes, Clear All
               </button>
             </div>
           </div>
         </div>
       )}

             {/* Road Detection Category Radio Buttons */}
       <div style={{ marginBottom: '30px' }}>
       <div className="radio-container" style={{ marginBottom: '10px' }}>
        <p className='pHeader'>Select LiDAR Type:</p>
        <div className='radio-content DF'>
          <label className="label-time-selector">
            <input
              type="radio"
              name="lidarType"
              value="aerial"
              checked={lidarType === 'aerial'}
              onChange={(e) => setLidarType(e.target.value)}
            />
            <span className="custom-radio-circle"></span>
            <span className="label-text">Aerial</span>
          </label>
          <label className="label-time-selector">
            <input
              type="radio"
              name="lidarType"
              value="mobile"
              checked={lidarType === 'mobile'}
              onChange={(e) => setLidarType(e.target.value)}
            />
            <span className="custom-radio-circle"></span>
            <span className="label-text">Mobile</span>
          </label>
        </div>
      </div> 
      </div>  
          <div className='content tab-content PB30' style={{ marginTop: '25px !important' }}>
          <p className='pHeader' htmlFor="groupSize">Files per LAS/LAZ chunk:</p>
         <input
           type="number"
           id="groupSize"
           min="1"
           value={groupSize}
           onChange={(e) => setGroupSize(parseInt(e.target.value, 10) || 1)}
           style={{ width: '120px'}}
         />
       </div>
    </form>

        </div>
      </div>
      
      {/* Right side panels - Summary and TabsRSA side by side */}
      <div className="right-panels">
        {/* Summary Component - Always visible on the right side */}
       
        
        {/* Horizontal/Embankment Tabs - Side by side with summary */}
        {(services.includes('horizontal') || services.includes('embankment')) && (
          <TabsRSA
            services={services}
            lidarType={lidarType}
            onSaveMap={{
              horizontal: setHorizontalData,
              embankment: setEmbankmentData
            }}
          />
        )}
                                     <RSASummary
            project={project}
            newProjectName={newProjectName}
            existingProjectName={existingProjectName}
            selectedProjectId={selectedProjectId}
            services={services}
            analysisTypes={analysisTypes}
            selectedFolder={selectedFolder}
            selectedDriveName={selectedDriveName}
            roadDetectionCategory={roadDetectionCategory}
            lidarType={lidarType}
            horizontalData={horizontalData}
            EmbankmentData={EmbankmentData}
            groupSize={groupSize}
            onSubmit={handleSubmit}
            onClear={handleClearClick}
          />
      </div>
      
      
    </div>
  );
}

export default PayloadForm;
